<template>
  <div>
    <h1 class="h1">{{ $t('onboarding_community_selection_title') }}</h1>
    <h4 class="h4">{{ $t('onboarding_community_selection_subtitle') }}</h4>
    <p v-if="errors.message" class="form-group__error" v-html="errors.message"></p>

    <form @submit.prevent="submit" :key="componentKey">
      <div class="communities-scroll-wrapper">
        <div class="communities-selection">
          <div
            v-for="community in availableCommunities"
            :key="community.id"
            class="community-option"
            :class="{ 'selected': selectedCommunityId === community.id }"
            @click="selectCommunity(community.id)"
          >
            <div class="community-option__image">
              <img
                :src="getImageForCommunity(community)"
                :alt="community.name"
                @error="handleImageError"
              />
            </div>
            <div class="community-option__content">
              <h5 class="community-option__name">{{ community.name }}</h5>
              <p class="community-option__description">{{ community.description.slice(0, 100) }}</p>
            </div>
            <div class="community-option__check">
              <Icon
                name="check"
                v-if="selectedCommunityId === community.id"
                class="check-icon"
              />
            </div>
          </div>

        </div>
      </div>

      <p v-if="errors.community" class="form-group__error">{{ errors.community }}</p>

      <form-group label="hidden" is-simplified>
        <div class="tw-flex items-center gap-4">
          <ds-button
            variant="rounded"
            :label="$t('actor_detail_next')"
            type="submit"
            :disabled="loading || !selectedCommunityId"
            :loading="loading"
          />
          <button type="button" class="skip-button" @click="skip" :disabled="loading">
            {{ $t('onboarding_community_skip') }}
          </button>
        </div>
      </form-group>
    </form>
  </div>
</template>

<script lang="ts">
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import { defineComponent } from 'vue'
  import { ACTION_TYPES as COMMUNITY_ACTION_TYPES } from '../../store/modules/communities.js'
  import { getImageForCommunity } from '../../util/helpers.js'
  import Icon from '../../components/Icon/Icon.vue'

  export default defineComponent({
    props: {
      registrationData: Object,
    },
    emits: ['updateRegistrationData', 'next'],
    components: {
      Icon
    },
    data() {
      return {
        componentKey: 0,
        selectedCommunityId: null,
        loading: false,
        errors: {},
      }
    },
    computed: {
      availableCommunities() {
        const allCommunities = this.$store.state.communities.listData.data || []
        // Filter to only show communities marked for onboarding
        return allCommunities.filter(community => community.usedInOnboarding)
      },
      communitiesLoading() {
        return this.$store.state.communities.listData.loading
      },
      isKlikEcosystem() {
        return this.$store.getters.isKlik
      }
    },
    async mounted() {
      // Load communities if not already loaded and this is the klik ecosystem
      if (this.isKlikEcosystem && !this.availableCommunities.length && !this.communitiesLoading) {
        await this.$store.dispatch(COMMUNITY_ACTION_TYPES.FETCH_COMMUNITIES_LIST)
      }
    },
    methods: {
      getImageForCommunity,
      skip() {
        this.selectedCommunityId = null
        this.submit()
      },
      selectCommunity(communityId) {
        this.selectedCommunityId = communityId
        this.errors = {}
      },
      handleImageError(event) {
        // Set a default image if community image fails to load
        event.target.src = '/images/default-community.png'
      },
      async submit() {
        this.errors = {}
        this.loading = true

        try {
          // Update registration data with selected community and proceed to next step
          const updatedRegistrationData = {
            ...this.registrationData,
            selectedCommunityId: this.selectedCommunityId
          }

          this.$emit('updateRegistrationData', updatedRegistrationData)
          // Note: updateRegistrationData already calls next(), so we don't emit 'next' here

        } catch (error) {
          console.error('Community selection failed:', error)
          this.errors.message = this.$t('onboarding_community_selection_error')
        } finally {
          this.loading = false
        }
      }
    },
    mixins: [OnboardingMixin],
  })
</script>

<style lang="scss" scoped>
  @import "../../../scss/variables";

  .communities-scroll-wrapper {
    position: relative;
    margin: 20px 0;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 48px;
      background: linear-gradient(to bottom, transparent, white);
      pointer-events: none;
      border-radius: 0 0 8px 8px;
    }
  }

  .communities-selection {
    max-height: 320px;
    overflow-y: auto;
    padding-right: 4px;

    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #d1d5db;
      border-radius: 4px;
    }
  }

  .community-option {
    display: flex;
    align-items: center;
    padding: 16px;
    margin-bottom: 12px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: white;

    &:hover {
      border-color: #d1d5db;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &.selected {
      border-color: var(--primary-color, #3b82f6);
      background: var(--primary-color-lightest, #eff6ff);
    }

    &__image {
      width: 48px;
      height: 48px;
      margin-right: 16px;
      flex-shrink: 0;
      border-radius: 6px;
      overflow: hidden;
      background: #f3f4f6;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__content {
      flex: 1;
      min-width: 0;
    }

    &__name {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 600;
      color: #374151;
      line-height: 1.2;
    }

    &__description {
      margin: 0;
      font-size: 14px;
      color: #6b7280;
      line-height: 1.4;
    }

    &__check {
      width: 24px;
      height: 24px;
      margin-left: 16px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      .check-icon {
        color: var(--primary-color, #3b82f6);
        font-size: 18px;
      }
    }
  }

  .skip-button {
    background: none;
    border: none;
    padding: 0;
    font-size: 14px;
    color: #6b7280;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      color: #374151;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @media (max-width: 768px) {
    .community-option {
      padding: 12px;

      &__image {
        width: 40px;
        height: 40px;
        margin-right: 12px;
      }

      &__name {
        font-size: 15px;
      }

      &__description {
        font-size: 13px;
      }

      &__check {
        margin-left: 12px;
      }
    }
  }
</style>
